import { faFaceSmile } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useState } from "react";
import { Button } from "frontend-components/components/ui/button";
import {
    ReactionUserList,
    ReactionUserListContainer,
} from "frontend-components/components/ui/reaction-user-list";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "frontend-components/components/ui/dialog";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "frontend-components/components/ui/hover-card";
import {
    Popover,
    PopoverContent,
    PopoverTitle,
    PopoverTrigger,
} from "frontend-components/components/ui/popover";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "frontend-components/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "frontend-components/components/ui/tabs";
import {
    REACTION_EMOJIS,
    REACTION_TYPES,
    type AttributedReaction,
    type ReactionCount,
    type ReactionType,
    type ReactionUser,
} from "frontend-components/lib/reactions";
import { cn } from "frontend-components/lib/utils";

interface ReactionBarLabels {
    addReaction: string;
    chooseReaction: string;
    loadingReactions: string;
    reactionsLoadError: string;
    noReactions: string;
    viewReactions: string;
    reactionDetails: string;
    allReactions: string;
    reactions: Record<ReactionType, string>;
}

interface ReactionBarProps extends Omit<React.ComponentProps<"div">, "onSelect"> {
    reactionCounts: readonly ReactionCount[];
    currentUserReaction: ReactionType | null;
    onSelect: (type: ReactionType) => void;
    onAttributionOpen?: () => void;
    isPending?: boolean;
    attributedReactions?: readonly AttributedReaction[];
    getUserAvatarUrl?: (user: ReactionUser) => string | undefined;
    isAttributionLoading?: boolean;
    isAttributionError?: boolean;
    labels: ReactionBarLabels;
}

interface ReactionOption {
    type: ReactionType;
    emoji: string;
}

const REACTIONS: readonly ReactionOption[] = REACTION_TYPES.map((type) => ({
    type,
    emoji: REACTION_EMOJIS[type],
}));

function ReactionBar({
    reactionCounts,
    currentUserReaction,
    onSelect,
    onAttributionOpen,
    isPending = false,
    attributedReactions = [],
    getUserAvatarUrl,
    isAttributionLoading = false,
    isAttributionError = false,
    labels,
    className,
    ...props
}: ReactionBarProps) {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [isReactionDetailsOpen, setIsReactionDetailsOpen] = useState(false);
    const countsByType = new Map<ReactionType, number>(
        reactionCounts.map(({ type, count }) => [type, count]),
    );
    const visibleReactions = REACTIONS.filter(
        (reaction) => (countsByType.get(reaction.type) ?? 0) > 0,
    );
    const attributedReactionsByType = new Map(
        attributedReactions.map((reaction) => [reaction.type, reaction]),
    );
    const allReactionUsers = visibleReactions.flatMap((reaction) =>
        (attributedReactionsByType.get(reaction.type)?.users ?? []).map((user) => ({
            user,
            emoji: reaction.emoji,
        })),
    );

    const handleSelect = (type: ReactionType) => {
        onSelect(type);
        setIsPickerOpen(false);
    };

    return (
        <div
            data-slot="reaction-bar"
            className={cn(
                "flex flex-wrap items-center gap-2 border-t border-compendia-border px-5 py-3",
                className,
            )}
            {...props}
        >
            {visibleReactions.map((reaction) => {
                const count = countsByType.get(reaction.type) ?? 0;
                const isSelected = currentUserReaction === reaction.type;
                const attributedReaction = attributedReactionsByType.get(reaction.type);

                return (
                    <HoverCard
                        key={reaction.type}
                        onOpenChange={(open) => {
                            if (open) onAttributionOpen?.();
                        }}
                    >
                        <HoverCardTrigger
                            render={
                                <Button
                                    type="button"
                                    variant="ghost"
                                    aria-pressed={isSelected}
                                    aria-label={`${labels.reactions[reaction.type]} (${count})`}
                                    disabled={isPending}
                                    className={
                                        isSelected
                                            ? "h-8 gap-1.5 rounded-full border border-chameleon-green bg-chameleon-green-10 px-3 py-1 text-label hover:bg-chameleon-green-20"
                                            : "h-8 gap-1.5 rounded-full border border-compendia-border bg-compendia-card px-3 py-1 text-label hover:bg-compendia-greige-20"
                                    }
                                    onClick={() => handleSelect(reaction.type)}
                                >
                                    <span aria-hidden="true" className="text-body-lg leading-none">
                                        {reaction.emoji}
                                    </span>
                                    <span>{count}</span>
                                </Button>
                            }
                        />

                        <HoverCardContent align="start" className="pointer-coarse:hidden">
                            <h3 className="border-b border-compendia-border px-1 pb-2 text-label font-medium">
                                <span aria-hidden="true" className="mr-2">
                                    {reaction.emoji}
                                </span>
                            </h3>

                            <ReactionUserListContainer>
                                {isAttributionLoading ? (
                                    <p className="px-1 py-1.5 text-compendia-muted">
                                        {labels.loadingReactions}
                                    </p>
                                ) : isAttributionError ? (
                                    <p className="px-1 py-1.5 text-compendia-muted">
                                        {labels.reactionsLoadError}
                                    </p>
                                ) : attributedReaction && attributedReaction.users.length > 0 ? (
                                    <ReactionUserList
                                        items={attributedReaction.users.map((user) => ({ user }))}
                                        getUserAvatarUrl={getUserAvatarUrl}
                                    />
                                ) : (
                                    <p className="px-1 py-1.5 text-compendia-muted">
                                        {labels.noReactions}
                                    </p>
                                )}
                            </ReactionUserListContainer>
                        </HoverCardContent>
                    </HoverCard>
                );
            })}

            <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
                <TooltipProvider delay={300}>
                    <Tooltip>
                        <PopoverTrigger
                            render={
                                <TooltipTrigger
                                    render={
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-sm"
                                            aria-label={labels.addReaction}
                                            disabled={isPending}
                                        >
                                            <FontAwesomeIcon icon={faFaceSmile} />
                                        </Button>
                                    }
                                />
                            }
                        />

                        <TooltipContent side="top">{labels.addReaction}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                <PopoverContent side="top" align="start" className="w-auto flex-row gap-1 p-2">
                    <PopoverTitle className="sr-only">{labels.chooseReaction}</PopoverTitle>

                    {REACTIONS.map((reaction) => {
                        const isSelected = currentUserReaction === reaction.type;

                        return (
                            <Button
                                key={reaction.type}
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-pressed={isSelected}
                                aria-label={labels.reactions[reaction.type]}
                                disabled={isPending}
                                className={
                                    isSelected
                                        ? "bg-chameleon-green-20 text-body-lg"
                                        : "text-body-lg"
                                }
                                onClick={() => handleSelect(reaction.type)}
                            >
                                <span aria-hidden="true" className="leading-none">
                                    {reaction.emoji}
                                </span>
                            </Button>
                        );
                    })}
                </PopoverContent>
            </Popover>

            {visibleReactions.length > 0 && (
                <Dialog
                    open={isReactionDetailsOpen}
                    onOpenChange={(open) => {
                        setIsReactionDetailsOpen(open);
                        if (open) onAttributionOpen?.();
                    }}
                >
                    <DialogTrigger
                        render={
                            <Button
                                type="button"
                                variant="outline"
                                className="hidden h-8 px-3 py-1 text-label pointer-coarse:inline-flex"
                            >
                                {labels.viewReactions}
                            </Button>
                        }
                    />
                    <DialogContent
                        className="max-h-[calc(100dvh-2rem)] gap-4 p-4 sm:max-w-sm"
                        onBackdropClick={() => setIsReactionDetailsOpen(false)}
                    >
                        <DialogHeader className="pr-10">
                            <DialogTitle>{labels.reactionDetails}</DialogTitle>
                        </DialogHeader>

                        <Tabs defaultValue="all" className="min-w-0 gap-3">
                            <div className="overflow-x-auto pb-1 [scrollbar-width:thin]">
                                <TabsList className="w-max">
                                    <TabsTrigger value="all">{labels.allReactions}</TabsTrigger>
                                    {visibleReactions.map((reaction) => {
                                        const count = countsByType.get(reaction.type) ?? 0;

                                        return (
                                            <TabsTrigger
                                                key={reaction.type}
                                                value={reaction.type}
                                                aria-label={`${labels.reactions[reaction.type]} (${count})`}
                                            >
                                                <span aria-hidden="true">{reaction.emoji}</span>
                                                <span>{count}</span>
                                            </TabsTrigger>
                                        );
                                    })}
                                </TabsList>
                            </div>

                            <TabsContent value="all">
                                <ReactionUserListContainer>
                                    {isAttributionLoading ? (
                                        <p className="px-1 py-1.5 text-compendia-muted">
                                            {labels.loadingReactions}
                                        </p>
                                    ) : isAttributionError ? (
                                        <p className="px-1 py-1.5 text-compendia-muted">
                                            {labels.reactionsLoadError}
                                        </p>
                                    ) : allReactionUsers.length > 0 ? (
                                        <ReactionUserList
                                            items={allReactionUsers}
                                            getUserAvatarUrl={getUserAvatarUrl}
                                        />
                                    ) : (
                                        <p className="px-1 py-1.5 text-compendia-muted">
                                            {labels.noReactions}
                                        </p>
                                    )}
                                </ReactionUserListContainer>
                            </TabsContent>

                            {visibleReactions.map((reaction) => {
                                const reactionUsers =
                                    attributedReactionsByType.get(reaction.type)?.users ?? [];

                                return (
                                    <TabsContent key={reaction.type} value={reaction.type}>
                                        <ReactionUserListContainer>
                                            {isAttributionLoading ? (
                                                <p className="px-1 py-1.5 text-compendia-muted">
                                                    {labels.loadingReactions}
                                                </p>
                                            ) : isAttributionError ? (
                                                <p className="px-1 py-1.5 text-compendia-muted">
                                                    {labels.reactionsLoadError}
                                                </p>
                                            ) : reactionUsers.length > 0 ? (
                                                <ReactionUserList
                                                    items={reactionUsers.map((user) => ({
                                                        user,
                                                        emoji: reaction.emoji,
                                                    }))}
                                                    getUserAvatarUrl={getUserAvatarUrl}
                                                />
                                            ) : (
                                                <p className="px-1 py-1.5 text-compendia-muted">
                                                    {labels.noReactions}
                                                </p>
                                            )}
                                        </ReactionUserListContainer>
                                    </TabsContent>
                                );
                            })}
                        </Tabs>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

export { ReactionBar };
export type { ReactionBarLabels, ReactionBarProps, ReactionCount };
